// Settings › General (Console D, APS-319): the project (its name, which owners and admins rename
// here, APS-394; its platforms, linking to Apps and keys; its id and organization) and, beside it,
// the organization's facts that used to sit under the profile. Owners and admins delete the project
// here, typing its name (APS-401): it leaves every list at once, so the organization's projects
// being deleted are listed here too, each with Cancel until it runs. A project being deleted has no
// page of its own meanwhile: the shell says so (APS-406), with its date and Cancel.
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useEffect, useId, useState } from 'react'
import { isApiError } from '../api/client'
import {
  appsQuery,
  orgQuery,
  projectsQuery,
  useRenameProject,
  useScheduleProjectErasure,
} from '../api/queries'
import type { ProjectErasure } from '../api/types'
import { ActionError } from '../components/ActionError'
import { Confirm } from '../components/Confirm'
import { Btn, PageHead, Section, Status, useToast } from '../components/d'
import { ProjectDeletions, useProjectDeletions } from '../components/ProjectDeletions'
import { PLATFORM_LABEL } from '../components/Shell'
import { ErasingNote, ErrorState, Loading } from '../components/states'
import { PLAN_KEY, trialState } from '../components/TrialMeter'
import { useI18n } from '../i18n'
import { flash } from '../lib/flash'
import { day, shortId } from '../lib/format'
import { KeyCell } from './AppsSettings'
import { retention } from './PlanSettings'

const ROLE_KEY = { owner: 'role.owner', admin: 'role.admin', member: 'role.member' } as const
/** What the confirmation says; the database's grace period decides (7 days). */
const GRACE_DAYS = 7

export function GeneralSettings({
  orgId,
  projectId,
  crumb,
  title,
}: {
  orgId: string
  projectId: string
  crumb: string
  title: string
}) {
  const i18n = useI18n()
  const { t } = i18n
  const { toast, show } = useToast()
  const projects = useQuery(projectsQuery(orgId))
  const apps = useQuery(appsQuery(orgId, projectId))
  const org = useQuery(orgQuery(orgId))
  const project = projects.data?.find((p) => p.id === projectId)
  const platforms = [...new Set((apps.data ?? []).map((a) => a.platform))]
    .map((p) => PLATFORM_LABEL[p])
    .join(' · ')
  const trial = org.data ? trialState(org.data) : undefined
  const params = { orgId, projectId }
  const manager = !!org.data && org.data.role !== 'member'
  const rename = useRenameProject(orgId, projectId)
  const [name, setName] = useState('')
  useEffect(() => setName(project?.name ?? ''), [project?.name])
  const nameId = useId()
  const draft = name.trim()
  const changed = !!draft && draft !== project?.name
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (changed) rename.mutate(draft)
  }
  const appsLink = (
    <Link
      className="ul"
      to="/o/$orgId/p/$projectId/settings/$section"
      params={{ ...params, section: 'apps' }}
    >
      {t('settings.tab.apps')}
    </Link>
  )
  // Every member sees the deletions and their dates; owners and admins cancel them.
  const deletions = useProjectDeletions(orgId, !!org.data)
  const open = deletions.data?.erasures ?? []
  // This project's own deletion: hidden from the lists, its General page says so (the shell's
  // note, APS-406), with its date and, for owners and admins, Cancel as the one action.
  const own = open.find((e) => e.project_id === projectId)
  const qc = useQueryClient()
  const navigate = useNavigate()
  const remove = useScheduleProjectErasure(orgId, projectId)
  const removed = async (erasure: ProjectErasure) => {
    // On to the organization's next project's General page, where the deletion is listed with
    // Cancel; with none left, to the new-project page, which lists it too. The toast follows.
    const rest = await qc.fetchQuery(projectsQuery(orgId))
    const next = rest[0]
    if (next)
      await navigate({
        to: '/o/$orgId/p/$projectId/settings/$section',
        params: { orgId, projectId: next.id, section: 'general' },
      })
    else await navigate({ to: '/o/$orgId/new-project', params: { orgId } })
    flash(
      t('settings.general.delete.done', {
        project: erasure.project_name,
        date: day(i18n, erasure.scheduled_until),
      }),
    )
  }

  // This project being deleted: only the note, its date and Cancel; nothing to rename or delete.
  if (!project && own)
    return (
      <main id="main" className="pg se-pg general">
        <PageHead
          crumb={crumb}
          title={title}
          meta={<span translate="no">{own.project_name}</span>}
        />
        <ErasingNote subject="project">
          <ProjectDeletions orgId={orgId} erasures={[own]} canCancel={manager} buttonClass="btn" />
        </ErasingNote>
        {toast}
      </main>
    )

  return (
    <main id="main" className="pg se-pg general">
      <PageHead
        crumb={crumb}
        title={title}
        meta={
          project ? (
            <>
              <span translate="no">{project.name}</span>
              {` · ${t('settings.general.created', { date: day(i18n, project.created_at) })}`}
            </>
          ) : undefined
        }
      />
      <div className="grid2">
        <div className="sections">
          <Section title={t('settings.general.project')}>
            {projects.isPending ? (
              <Loading />
            ) : projects.isError ? (
              <ErrorState error={projects.error} retry={() => projects.refetch()} />
            ) : project ? (
              <>
                <form className="se-sp" onSubmit={submit}>
                  {manager ? (
                    <label className="lbl" htmlFor={nameId}>
                      {t('settings.org.name')}
                      <input
                        id={nameId}
                        className="input narrow"
                        maxLength={100}
                        required
                        translate="no"
                        value={name}
                        onChange={(e) => {
                          setName(e.target.value)
                          rename.reset()
                        }}
                      />
                    </label>
                  ) : (
                    <div className="lbl">
                      {t('settings.org.name')}
                      <span className="se-p" translate="no">
                        {project.name}
                      </span>
                    </div>
                  )}
                  <div className="lbl">
                    {t('settings.general.platforms')}
                    <span className="se-p">
                      {apps.isPending ? '…' : platforms || t('settings.general.noApps')} {appsLink}
                    </span>
                  </div>
                  {manager ? (
                    <div>
                      {/* Solid only with a name to save; until then the plain disabled button. */}
                      <Btn
                        type="submit"
                        variant={changed ? 'solid' : undefined}
                        disabled={rename.isPending || !changed}
                      >
                        {t('settings.general.save')}
                      </Btn>
                    </div>
                  ) : null}
                  {rename.isSuccess ? (
                    <p role="status" className="se-hint">
                      <Status s="ok" label={t('settings.general.saved')} />
                    </p>
                  ) : rename.error ? (
                    <p role="alert" className="se-hint">
                      <Status
                        s="miss"
                        label={
                          isApiError(rename.error, 409)
                            ? t('settings.general.deleting')
                            : isApiError(rename.error, 429)
                              ? t('settings.rateLimited')
                              : t('state.failed')
                        }
                      />
                    </p>
                  ) : null}
                </form>
                <dl className="kv">
                  <dt>{t('settings.general.projectId')}</dt>
                  <dd>
                    <KeyCell
                      value={project.id}
                      shown={shortId(project.id, 8, 4)}
                      onCopied={(ok) => show(ok ? t('d.code.copied') : t('d.code.copyFailed'))}
                    />
                  </dd>
                  <dt>{t('settings.org')}</dt>
                  <dd translate="no">{org.data?.name ?? '…'}</dd>
                </dl>
              </>
            ) : null}
          </Section>
          {project && manager ? (
            <Section title={t('settings.general.danger')}>
              <div className="se-danger">
                <div>
                  <b>{t('settings.general.delete.title')}</b>
                  <p>
                    {t('settings.general.delete.body', {
                      project: project.name,
                      org: org.data?.name ?? '',
                      days: GRACE_DAYS,
                    })}
                  </p>
                </div>
                <Confirm
                  buttonClass="btn danger"
                  confirmClass="btn danger solid"
                  label={t('settings.general.delete.action')}
                  title={t('settings.general.delete.confirmTitle', { project: project.name })}
                  body={t('settings.general.delete.confirmBody', {
                    date: day(i18n, new Date(Date.now() + GRACE_DAYS * 86_400_000).toISOString()),
                    days: GRACE_DAYS,
                  })}
                  confirm={t('settings.general.delete.action')}
                  typed={{
                    label: t('settings.general.delete.type', { project: project.name }),
                    value: project.name,
                  }}
                  busy={remove.isPending}
                  onConfirm={() => remove.mutate(project.name, { onSuccess: removed })}
                />
              </div>
              {remove.error ? <ActionError error={remove.error} /> : null}
            </Section>
          ) : null}
        </div>
        <aside className="sections" aria-label={t('settings.org')}>
          <Section title={t('settings.org')} meta={org.data?.name}>
            {org.isPending ? (
              <Loading />
            ) : org.isError ? (
              <ErrorState error={org.error} retry={() => org.refetch()} />
            ) : (
              <>
                <dl className="kv">
                  <dt>{t('settings.org.name')}</dt>
                  <dd translate="no">{org.data.name}</dd>
                  <dt>{t('settings.org.plan')}</dt>
                  <dd>{t(PLAN_KEY[org.data.plan])}</dd>
                  {trial && org.data.trial_ends_at ? (
                    <>
                      <dt>{t('settings.org.trialEnds')}</dt>
                      <dd>{day(i18n, org.data.trial_ends_at)}</dd>
                    </>
                  ) : null}
                  <dt>{t('settings.org.retention')}</dt>
                  <dd>{retention(t, org.data.retention_days)}</dd>
                  <dt>{t('plans.strip.crash')}</dt>
                  <dd>{retention(t, org.data.crash_retention_days)}</dd>
                  <dt>{t('settings.org.role')}</dt>
                  <dd>{t(ROLE_KEY[org.data.role])}</dd>
                </dl>
                <p className="se-hint" style={{ paddingTop: 10 }}>
                  <Link
                    className="ul"
                    to="/o/$orgId/p/$projectId/settings/$section"
                    params={{ ...params, section: 'plan' }}
                  >
                    {t('settings.tab.plan')}
                  </Link>
                  {' · '}
                  <Link
                    className="ul"
                    to="/o/$orgId/p/$projectId/settings/$section"
                    params={{ ...params, section: 'members' }}
                  >
                    {t('settings.tab.members')}
                  </Link>
                  {' · '}
                  <Link
                    className="ul"
                    to="/o/$orgId/p/$projectId/settings/$section"
                    params={{ ...params, section: 'profile' }}
                  >
                    {t('settings.general.yourProfile')}
                  </Link>
                </p>
              </>
            )}
          </Section>
          {open.filter((e) => e !== own).length > 0 ? (
            <Section title={t('settings.general.deleting.title')}>
              <ProjectDeletions
                orgId={orgId}
                erasures={open.filter((e) => e !== own)}
                canCancel={manager}
              />
            </Section>
          ) : null}
        </aside>
      </div>
      {toast}
    </main>
  )
}
